import React, { useEffect } from "react";
import { Column, Row, Text, Icon, Card } from "@amzn/meridian";
import chevronLeftLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-left-large";
import clipboardTokens from "@amzn/meridian-tokens/base/icon/clipboard";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "src/store/store";
import { createWorkflowInspection, getInspectionWorkflow, setActiveWorkflow } from "src/store/workflowSlice";
import { resetInspectionCaptureState } from "src/store/inspectionCaptureSlice";
import { getCheckpoints } from "./utils/checkpointExtractor";
import ThemedButton from "./inspectionReview/ThemedButton";
import CheckpointCard from "./CheckpointCard";
import "./checkpointsPage.scss";

const CheckpointsPage = () => {
  const { workflowTemplateId } = useParams<{ workflowTemplateId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { workflows, activeWorkflow, questions } = useAppSelector((state) => state.workflow);
  const { flags } = useAppSelector((state) => state.featureFlags);

  useEffect(() => {
    if (activeWorkflow === null) {
      dispatch(getInspectionWorkflow());
    }
  }, []);

  // Set active workflow from URL param once workflows are loaded
  useEffect(() => {
    if (workflows.length > 0 && workflowTemplateId) {
      const workflow = workflows.find((wf) => wf.template_id === workflowTemplateId);
      if (workflow) {
        dispatch(setActiveWorkflow(workflow));
      }
    }
  }, [workflows, workflowTemplateId]);

  const checkpoints = activeWorkflow ? getCheckpoints(activeWorkflow, questions) : [];
  const checkpointCount = checkpoints.length;

  return (
    <div className="checkpoints-page pwa-scrollable">
      <Column spacingInset="500" spacing="400">
        <Row alignmentVertical="center" spacing="300">
          <div
            onClick={() => navigate(flags.opsLive ? "/home" : "/configurationDrivenInspections")
            }
            role="button"
          >
            <Icon tokens={chevronLeftLargeTokens} color="primary" />
          </div>
          <Text type="b300" color="primary">
            Inspection Setup
          </Text>
        </Row>

        <Text type="h300" color="primary">
          {activeWorkflow?.name ?? `Inspection`}
        </Text>

        <Text type="b200" color="secondary">
          Review all checkpoints before starting
        </Text>

        <Card spacingInset="400">
          <Row alignmentHorizontal="justify" className="checkpoints-page__info">
            <Row>
              <Icon tokens={clipboardTokens} color="success" />
              <Text type="h50">{checkpointCount} Checkpoints</Text>
            </Row>
            <Text type="b200" color="secondary">Estimated Time: <Text type="h50">5-7 minutes</Text></Text>
          </Row>
        </Card>


        {checkpoints.map((checkpoint, index) => {
          const number = String(index + 1).padStart(2, "0");
          return (
            <CheckpointCard
              key={checkpoint.question_id}
              number={number}
              text={checkpoint.text}
              mode={checkpoint.mode}
            />
          );
        })}
        <Text type="b100" color="secondary" alignment="center">
          <b>Disclaimer:</b> The questions shown here represent the complete
          inspection questions for reference. In the next step, you
          will not be required to answer each question individually.
          Instead, SafetyGenie will guide you through an AI-driven
          walkthrough of the cage inspection, prompting you to move
          naturally through the space. Based on your walkthrough and
          captured inputs, all underlying inspection questions will
          be automatically assessed and answered in the background.
        </Text>
      </Column>
      {/* 
          This empty row is added in order to provide empty space between the last
          checkpoint card and the fixed position themed button to start the inspection
      */}
      <Row minHeight="50px"></Row>

      {checkpoints.length !== 0 &&
        <Row className="checkpoints-page__footer" spacingInset="400" width="100%" widths="fill">
          <ThemedButton
            onClick={async () => {
              dispatch(resetInspectionCaptureState());
              await dispatch(createWorkflowInspection({
                templateId: activeWorkflow!.template_id,
                templateName: activeWorkflow!.name,
              }));
              navigate("/configurationDrivenInspections/execute")
            }}
          >
            Start Inspection
          </ThemedButton>
        </Row>}
    </div>
  );
};

export default CheckpointsPage;
